/* TCB iOS Drum Roll Picker — mobile MTM measurement fields
 *
 * UX flow:
 *   1. Each MTM <input> is hidden; a compact tappable chip (.tcb-meas-btn)
 *      replaces it visually — fits on one line with its label.
 *   2. Tapping a chip opens a shared bottom-sheet modal with a single
 *      scroll-snap drum pre-loaded for that field's range.
 *   3. User spins the drum, taps Done (or backdrop) → value is written
 *      back to the hidden <input> and shown on the chip.
 *
 * Activates only on touch devices.
 * All existing form-submit code reads input.value unchanged.
 */
(function () {
  'use strict';

  /* Public helper: reset a measurement input's mobile "chip" back to its
     empty/placeholder state after its underlying value is cleared
     programmatically (e.g. a shared quick-add modal is reopened for a
     different product). Setting input.value directly does not update the
     chip on its own — this keeps them in sync. Safe no-op on desktop or for
     any input that never got a chip attached. */
  window.tcbResetMeasChip = function (input) {
    if (input && input._tcbBtn) {
      input._tcbBtn.classList.remove('is-set');
      input._tcbBtn.innerHTML = emptyChipHTML(input);
    }
  };

  if (!window.matchMedia('(hover: none) and (pointer: coarse)').matches) return;

  /* ─── Constants ─────────────────────────────────────────── */
  var ITEM_H = 40;          // px — comfortable tap/scroll row height
  var VISIBLE = 5;          // rows shown in the drum
  var PAD_H  = ITEM_H * 2; // top + bottom spacer so first/last item can centre

  /* ─── Measurement configs: { min, max, step, def } ──────── */
  var MEAS = {
    neck:         { min: 13, max: 20,   step: 0.5, def: 15.5 },
    chest:        { min: 32, max: 54,   step: 0.5, def: 42   },
    waist:        { min: 26, max: 50,   step: 0.5, def: 34   },
    sleeve:       { min: 28, max: 38,   step: 0.5, def: 34   },
    shoulder:     { min: 15, max: 22,   step: 0.5, def: 18   },
    length:       { min: 26, max: 35,   step: 0.5, def: 31   },
    bicep:        { min: 10, max: 20,   step: 0.5, def: 14   },
    seat:         { min: 32, max: 52,   step: 0.5, def: 40   },
    thigh:        { min: 18, max: 30,   step: 0.5, def: 23   },
    inseam:       { min: 26, max: 36,   step: 0.5, def: 32   },
    outseam:      { min: 36, max: 48,   step: 0.5, def: 42   },
    rise:         { min: 8,  max: 15,   step: 0.5, def: 11   },
    knee:         { min: 14, max: 22,   step: 0.5, def: 17   },
    hem_width:    { min: 12, max: 18,   step: 0.5, def: 15   },
    tr_hem:       { min: 12, max: 18,   step: 0.5, def: 15   },
    // Suit jacket variants
    jkt_chest:    { min: 32, max: 54,   step: 0.5, def: 42   },
    jkt_waist:    { min: 26, max: 50,   step: 0.5, def: 34   },
    jkt_seat:     { min: 32, max: 52,   step: 0.5, def: 40   },
    jkt_shoulder: { min: 15, max: 22,   step: 0.5, def: 18   },
    jkt_length:   { min: 26, max: 35,   step: 0.5, def: 31   },
    jkt_sleeve:   { min: 28, max: 38,   step: 0.5, def: 34   },
    jkt_bicep:    { min: 10, max: 20,   step: 0.5, def: 14   },
    // Suit trouser variants
    tr_waist:     { min: 26, max: 50,   step: 0.5, def: 34   },
    tr_seat:      { min: 32, max: 52,   step: 0.5, def: 40   },
    tr_thigh:     { min: 18, max: 30,   step: 0.5, def: 23   },
    tr_inseam:    { min: 26, max: 36,   step: 0.5, def: 32   },
    tr_outseam:   { min: 36, max: 48,   step: 0.5, def: 42   },
    tr_rise:      { min: 8,  max: 15,   step: 0.5, def: 11   },
    tr_knee:      { min: 14, max: 22,   step: 0.5, def: 17   },
    tr_hem_width: { min: 12, max: 18,   step: 0.5, def: 15   }
  };

  /* ─── Human-readable labels for the modal header ────────── */
  var KEY_LABELS = {
    neck: 'Neck', chest: 'Chest', waist: 'Waist', sleeve: 'Sleeve',
    shoulder: 'Shoulder', length: 'Body Length', bicep: 'Bicep',
    seat: 'Seat', thigh: 'Thigh', inseam: 'Inseam', outseam: 'Outseam',
    rise: 'Rise', knee: 'Knee', hem_width: 'Hem Width', tr_hem: 'Hem Width',
    jkt_chest: 'Jacket Chest', jkt_waist: 'Jacket Waist',
    jkt_seat: 'Jacket Seat', jkt_shoulder: 'Jacket Shoulder',
    jkt_length: 'Jacket Length', jkt_sleeve: 'Jacket Sleeve',
    jkt_bicep: 'Jacket Bicep',
    tr_waist: 'Trouser Waist', tr_seat: 'Trouser Seat',
    tr_thigh: 'Trouser Thigh', tr_inseam: 'Trouser Inseam',
    tr_outseam: 'Trouser Outseam', tr_rise: 'Trouser Rise',
    tr_knee: 'Trouser Knee', tr_hem_width: 'Trouser Hem'
  };

  /* ─── Input ID → measurement key (quick-add modals) ─────── */
  var ID_MAP = {
    mNeck: 'neck', mChest: 'chest', mWaist: 'waist',
    mSleeve: 'sleeve', mShoulder: 'shoulder', mLength: 'length',
    mBicep: 'bicep', mSeat: 'seat', mThigh: 'thigh',
    mInseam: 'inseam', mOutseam: 'outseam', mRise: 'rise',
    mKnee: 'knee', mHemWidth: 'hem_width',
    // Suit PDP tcbPlusMtmForm
    tpJktChest: 'jkt_chest', tpJktWaist: 'jkt_waist', tpJktSeat: 'jkt_seat',
    tpJktShoulder: 'jkt_shoulder', tpJktLength: 'jkt_length',
    tpJktSleeve: 'jkt_sleeve', tpJktBicep: 'jkt_bicep',
    tpTrWaist: 'tr_waist', tpTrSeat: 'tr_seat', tpTrThigh: 'tr_thigh',
    tpTrInseam: 'tr_inseam', tpTrOutseam: 'tr_outseam', tpTrRise: 'tr_rise',
    tpTrKnee: 'tr_knee', tpTrHem: 'tr_hem',
    // Suit blazer modal
    sbMChest: 'jkt_chest', sbMWaist: 'jkt_waist', sbMSeat: 'jkt_seat',
    sbMShoulder: 'jkt_shoulder', sbMLength: 'jkt_length',
    sbMSleeve: 'jkt_sleeve', sbMBicep: 'jkt_bicep',
    // Suit trouser modal
    stMWaist: 'tr_waist', stMSeat: 'tr_seat', stMThigh: 'tr_thigh',
    stMInseam: 'tr_inseam', stMOutseam: 'tr_outseam', stMRise: 'tr_rise',
    stMKnee: 'tr_knee', stMHem: 'tr_hem_width', stMHemW: 'tr_hem_width',
    // Suit quick-add from collection / PLP / search / favorites
    // (tcbBuildMeasForm injects inputs with mJk* / mTr* IDs dynamically)
    mJkChest: 'jkt_chest', mJkWaist: 'jkt_waist', mJkSeat: 'jkt_seat',
    mJkShoulder: 'jkt_shoulder', mJkLength: 'jkt_length',
    mJkSleeve: 'jkt_sleeve', mJkBicep: 'jkt_bicep',
    mTrWaist: 'tr_waist', mTrSeat: 'tr_seat', mTrThigh: 'tr_thigh',
    mTrInseam: 'tr_inseam', mTrOutseam: 'tr_outseam', mTrRise: 'tr_rise',
    mTrKnee: 'tr_knee', mTrHemWidth: 'tr_hem_width'
  };

  /* ─── Helpers ────────────────────────────────────────────── */
  function buildItems(cfg) {
    var items = [];
    var count = Math.round((cfg.max - cfg.min) / cfg.step);
    for (var i = 0; i <= count; i++) {
      var v = cfg.min + i * cfg.step;
      items.push(cfg.step < 1 ? v.toFixed(1) : String(Math.round(v)));
    }
    return items;
  }

  function getKey(input) {
    var df = input.getAttribute('data-field');
    if (df && MEAS[df]) return df;
    var id = input.id;
    if (id && ID_MAP[id]) return ID_MAP[id];
    return null;
  }

  function clamp(v, lo, hi) { return Math.max(lo, Math.min(hi, v)); }

  /* Empty-state chip markup. The wide trouser-length field shows its
     placeholder text inside the chip; all other fields show the chevron only. */
  function emptyChipHTML(input) {
    if (input && input.classList && input.classList.contains('tcb-hem-inseam-wide')) {
      var ph = input.getAttribute('placeholder') || 'Enter your trouser inseam';
      return '<span class="tcb-meas-btn-ph">' + ph + '</span><span class="tcb-meas-btn-hint">&#x25BE;</span>';
    }
    return '<span class="tcb-meas-btn-hint">&#x25BE;</span>';
  }

  /* ─── Shared modal state ─────────────────────────────────── */
  var modal, sheetEl, scrollEl, labelEl, clearBtn;
  var activeInput = null;
  var activeItems = [];
  var activeIdx   = 0;
  var scrollTimer;

  /* ─── Build the single shared modal (once) ───────────────── */
  function buildModal() {
    modal = document.createElement('div');
    modal.id = 'tcbPickerModal';
    modal.className = 'tcb-picker-modal';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');

    sheetEl = document.createElement('div');
    sheetEl.className = 'tcb-picker-sheet';

    /* Handle bar */
    var handle = document.createElement('div');
    handle.className = 'tcb-picker-handle';

    /* Header: label on left, Clear + Done on right */
    var header = document.createElement('div');
    header.className = 'tcb-picker-header';

    labelEl = document.createElement('span');
    labelEl.className = 'tcb-picker-label';

    var headerRight = document.createElement('div');
    headerRight.className = 'tcb-picker-header-right';

    clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'tcb-picker-clear';
    clearBtn.textContent = 'Clear';

    var doneBtn = document.createElement('button');
    doneBtn.type = 'button';
    doneBtn.className = 'tcb-picker-done';
    doneBtn.textContent = 'Done';

    headerRight.appendChild(clearBtn);
    headerRight.appendChild(doneBtn);
    header.appendChild(labelEl);
    header.appendChild(headerRight);

    /* Drum */
    var drum = document.createElement('div');
    drum.className = 'tcb-picker-drum';

    var maskTop = document.createElement('div');
    maskTop.className = 'tcb-picker-mask tcb-picker-mask-top';

    var maskBot = document.createElement('div');
    maskBot.className = 'tcb-picker-mask tcb-picker-mask-bot';

    var highlight = document.createElement('div');
    highlight.className = 'tcb-picker-highlight';

    scrollEl = document.createElement('div');
    scrollEl.className = 'tcb-picker-scroll';

    drum.appendChild(maskTop);
    drum.appendChild(maskBot);
    drum.appendChild(highlight);
    drum.appendChild(scrollEl);

    sheetEl.appendChild(handle);
    sheetEl.appendChild(header);
    sheetEl.appendChild(drum);
    modal.appendChild(sheetEl);
    document.body.appendChild(modal);

    /* ── Event bindings ── */
    // Backdrop tap → commit + close
    modal.addEventListener('click', function (e) {
      if (e.target === modal) commitAndClose();
    });

    // Done button
    doneBtn.addEventListener('click', function (e) {
      e.preventDefault();
      commitAndClose();
    });

    // Clear button — blanks the field
    clearBtn.addEventListener('click', function (e) {
      e.preventDefault();
      if (activeInput) {
        activeInput.value = '';
        activeInput.dispatchEvent(new Event('input',  { bubbles: true }));
        activeInput.dispatchEvent(new Event('change', { bubbles: true }));
        if (activeInput._tcbBtn) {
          activeInput._tcbBtn.innerHTML = emptyChipHTML(activeInput);
          activeInput._tcbBtn.classList.remove('is-set');
        }
      }
      closeModal();
    });

    /* Scroll → update visuals + track active index */
    scrollEl.addEventListener('scroll', function () {
      updateVisuals(scrollEl.scrollTop);
      clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () {
        activeIdx = clamp(Math.round(scrollEl.scrollTop / ITEM_H), 0, activeItems.length - 1);
      }, 80);
    }, { passive: true });

    scrollEl.addEventListener('scrollend', function () {
      clearTimeout(scrollTimer);
      activeIdx = clamp(Math.round(scrollEl.scrollTop / ITEM_H), 0, activeItems.length - 1);
    }, { passive: true });
  }

  /* ─── Visual: opacity + scaleY by distance from centre ───── */
  function updateVisuals(scrollTop) {
    var centre = scrollTop / ITEM_H;
    var els = scrollEl.querySelectorAll('.tcb-picker-item');
    els.forEach(function (el) {
      var dist = Math.abs(parseInt(el.dataset.idx, 10) - centre);
      var op, sc;
      if      (dist < 0.5) { op = 1.00; sc = 1.00; }
      else if (dist < 1.5) { op = 0.60; sc = 0.90; }
      else if (dist < 2.5) { op = 0.30; sc = 0.78; }
      else                  { op = 0.12; sc = 0.66; }
      el.style.opacity   = op;
      el.style.transform = 'scaleY(' + sc + ')';
    });
  }

  /* ─── Open modal for a specific input ───────────────────── */
  function openPicker(input) {
    var key = getKey(input);
    if (!key) return;

    var cfg  = MEAS[key];
    activeInput = input;
    activeItems = buildItems(cfg);

    // Determine starting index
    var curVal = input.value ? parseFloat(input.value).toFixed(1) : null;
    activeIdx = curVal ? activeItems.indexOf(curVal) : -1;
    if (activeIdx < 0) {
      var defVal = cfg.def.toFixed(1);
      activeIdx = activeItems.indexOf(defVal);
      if (activeIdx < 0) activeIdx = Math.floor(activeItems.length / 2);
    }

    // Set modal label
    labelEl.textContent = KEY_LABELS[key] || key.replace(/_/g, ' ');

    // Populate drum
    scrollEl.innerHTML = '';
    var padTop = document.createElement('div');
    padTop.className = 'tcb-picker-pad';
    scrollEl.appendChild(padTop);

    activeItems.forEach(function (val, i) {
      var el = document.createElement('div');
      el.className = 'tcb-picker-item';
      el.dataset.idx = i;
      el.textContent = val + '"';
      scrollEl.appendChild(el);
    });

    var padBot = document.createElement('div');
    padBot.className = 'tcb-picker-pad';
    scrollEl.appendChild(padBot);

    // Show modal — tiny delay lets display:flex render before transition
    modal.style.display = 'flex';
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        modal.classList.add('is-open');
        document.body.classList.add('tcb-picker-open');
        // Set scroll after sheet is visible (scrollTop on hidden element = noop)
        scrollEl.scrollTop = activeIdx * ITEM_H;
        updateVisuals(scrollEl.scrollTop);
      });
    });
  }

  /* ─── Commit current drum position and close ─────────────── */
  function commitAndClose() {
    if (!activeInput) { closeModal(); return; }
    activeIdx = clamp(Math.round(scrollEl.scrollTop / ITEM_H), 0, activeItems.length - 1);
    var val = activeItems[activeIdx];

    activeInput.value = val;
    activeInput.dispatchEvent(new Event('input',  { bubbles: true }));
    activeInput.dispatchEvent(new Event('change', { bubbles: true }));

    if (activeInput._tcbBtn) {
      activeInput._tcbBtn.textContent = val + '"';
      activeInput._tcbBtn.classList.add('is-set');
    }

    closeModal();
  }

  function closeModal() {
    modal.classList.remove('is-open');
    document.body.classList.remove('tcb-picker-open');
    activeInput = null;
    // Hide from DOM after transition (300ms) to avoid stacking-context issues
    setTimeout(function () {
      if (!modal.classList.contains('is-open')) {
        modal.style.display = 'none';
      }
    }, 320);
  }

  /* ─── Attach chip button to one input ───────────────────── */
  function attachChip(input) {
    if (input.dataset.pickerInit === '1') return;
    var key = getKey(input);
    if (!key) return;
    input.dataset.pickerInit = '1';
    input.style.display = 'none';

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tcb-meas-btn';
    // Wide trouser-length field gets its own class for placeholder + taller styling
    if (input.classList && input.classList.contains('tcb-hem-inseam-wide')) {
      btn.classList.add('tcb-meas-btn-wide');
    }

    // Display existing value, or a subtle chevron to signal "tap to select"
    if (input.value) {
      btn.textContent = parseFloat(input.value).toFixed(1) + '"';
      btn.classList.add('is-set');
    } else {
      btn.innerHTML = emptyChipHTML(input);
    }

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      openPicker(input);
    });

    // Keep a direct reference for value-writeback
    input._tcbBtn = btn;

    input.parentNode.insertBefore(btn, input.nextSibling);
  }

  /* ─── Selector covering every MTM input surface ─────────── */
  var SELECTORS = [
    '#mtmPanel .mtm-input',
    '.tcb-meas-field input',
    '.tcb-plus-meas-grid input',
    '#suitBlazerModal  input[type="number"]',
    '#suitBlazerModal  input[type="text"]',
    '#suitTrouserModal input[type="number"]',
    '#suitTrouserModal input[type="text"]',
    '#tcbPlusMtmForm   input[type="number"]',
    '#tcbPlusMtmForm   input[type="text"]',
    '.tcb-hem-inseam-input'
  ].join(', ');

  function initAll(root) {
    (root || document).querySelectorAll(SELECTORS).forEach(attachChip);
  }

  /* ─── MutationObserver: dynamic quick-add modals ─────────── */
  var mo = new MutationObserver(function (mutations) {
    mutations.forEach(function (m) {
      m.addedNodes.forEach(function (node) {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(SELECTORS)) { attachChip(node); return; }
        if (node.querySelectorAll) node.querySelectorAll(SELECTORS).forEach(attachChip);
      });
    });
  });

  mo.observe(document.body, { childList: true, subtree: true });

  /* ─── Bootstrap ──────────────────────────────────────────── */
  function boot() {
    buildModal();
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

}());
